import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";

import { checkMarkIcon as CheckMarkIcon } from "../../assets/icons";
import AuthRoleSwitch from "../../components/AuthRoleSwitch/AuthRoleSwitch";
import { FormModal } from "../../components/Modals";
import type { Field } from "../../components/Modals/FormModal/FormModal.types";
import SuccessModal from "../../components/Modals/SuccessModal";
import {
  createReferral,
  registerUser,
  sendVerificationCode,
} from "../../services/authService/authService";
import type { AuthRole } from "../../types/auth";
import { useNotify } from "context/NotificationContext";
import { getServerErrorData, getServerErrorMessage } from "utils/httpError";
import { logger } from "utils/logger";
import {
  LinkFootnote,
  LinkPageContent,
  LinkPageShell,
  LinkPageTitle,
  LinkPrimaryAction,
  LinkSectionTitle,
  SuccessIconCircle,
  SuccessState,
  SuccessText,
} from "../StudentPortal/StudentLinkPage.styles";
import { VerificationHint } from "./RegistrationPage.styles";

const SUCCESS_REDIRECT_DELAY_MS = 1600;
const STUDENT_CABINET_PATH = "/student/courses";

const sharedFields: Field[] = [
  { name: "firstName", type: "text", placeholder: "Имя", required: true, width: "369px" },
  { name: "lastName", type: "text", placeholder: "Фамилия", required: true, width: "369px" },
  {
    name: "email",
    type: "email",
    placeholder: "Почта",
    required: true,
    width: "369px",
    validate: (value) => (!/\S+@\S+\.\S+/.test(value) ? "Неправильный формат почты" : null),
  },
  {
    name: "password",
    type: "password",
    placeholder: "Пароль",
    required: true,
    width: "369px",
    validate: (value) => (value.length < 8 ? "Пароль должен содержать не менее 8 символов" : null),
  },
  {
    name: "confirmPassword",
    type: "password",
    placeholder: "Повторите пароль",
    required: true,
    width: "369px",
    validate: (value) => (value.length < 8 ? "Пароль должен содержать не менее 8 символов" : null),
  },
];

const verificationFields: Field[] = [
  {
    name: "verification_code",
    type: "text",
    placeholder: "Код из письма",
    required: true,
    width: "369px",
  },
];

const API_TO_FORM_FIELD: Record<string, string> = {
  first_name: "firstName",
  last_name: "lastName",
  confirm_password: "confirmPassword",
  organization_name: "organization_name",
  verification_code: "verification_code",
  email: "email",
  password: "password",
};

const normalizeErrorValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(String).join(". ");
  return String(value);
};

const openStudentCabinetInBrowser = (destination: string) => {
  const loginPath = `/login?role=student&next=${encodeURIComponent(destination)}`;
  const url = new URL(loginPath, window.location.origin).toString();
  const openLink = window.Telegram?.WebApp?.openLink;

  if (typeof openLink === "function") {
    openLink.call(window.Telegram.WebApp, url);
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
};

const RegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") === "student" ? "student" : "teacher";
  const linkToken = searchParams.get("link_token") || undefined;
  const next = searchParams.get("next") || "";
  const [role, setRole] = useState<AuthRole>(linkToken ? "student" : initialRole);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [registrationData, setRegistrationData] = useState<Record<string, string> | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const toast = useNotify();

  const registrationFields = useMemo<Field[]>(
    () =>
      role === "teacher"
        ? [
            {
              name: "organization_name",
              type: "text",
              placeholder: "Организация",
              required: true,
              width: "369px",
            } as Field,
            ...sharedFields,
          ]
        : [...sharedFields],
    [role],
  );

  const fields = isCodeSent ? verificationFields : registrationFields;
  const nextSuffix = next ? `&next=${encodeURIComponent(next)}` : "";
  const loginPath = `/login?role=${role}${nextSuffix}`;
  const isTelegramStudentSignup = role === "student" && Boolean(linkToken);
  const studentBrowserDestination = next.startsWith("/") ? next : STUDENT_CABINET_PATH;
  const footerLinks = isTelegramStudentSignup
    ? []
    : [{ text: "Войти в аккаунт", href: loginPath, variant: "primary" as const }];

  useEffect(() => {
    if (!showSuccessModal || isTelegramStudentSignup) return undefined;

    const redirectTimer = window.setTimeout(() => {
      navigate(loginPath, { replace: true });
    }, SUCCESS_REDIRECT_DELAY_MS);

    return () => window.clearTimeout(redirectTimer);
  }, [isTelegramStudentSignup, loginPath, navigate, showSuccessModal]);

  const setErrorsFromResponse = (error: unknown) => {
    const serverErrors = getServerErrorData(error);
    if (!serverErrors) return false;
    const formattedErrors: Record<string, string> = {};
    Object.entries(serverErrors).forEach(([key, value]) => {
      const formField = API_TO_FORM_FIELD[key];
      if (formField) formattedErrors[formField] = normalizeErrorValue(value);
    });
    setExternalErrors(formattedErrors);
    return Object.keys(formattedErrors).length > 0;
  };

  const handleSubmit = async (formData: Record<string, string>) => {
    setIsLoading(true);
    setExternalErrors({});
    try {
      if (!isCodeSent) {
        await sendVerificationCode(
          formData.email,
          "registration",
          role,
          formData.password,
          formData.confirmPassword,
        );
        setRegistrationData(formData);
        setIsCodeSent(true);
        return;
      }

      if (!registrationData) {
        setIsCodeSent(false);
        return;
      }

      const completeFormData = { ...registrationData, ...formData };
      const response = await registerUser(
        completeFormData,
        completeFormData.confirmPassword,
        role,
        linkToken,
      );
      if (role === "teacher") {
        const referralCode = Cookies.get("referralCode") || "";
        await createReferral(String(response.data.id), referralCode);
      }
      setShowSuccessModal(true);
    } catch (error) {
      setErrorsFromResponse(error);
      logger.error("Ошибка при регистрации:", error);
      toast.error(
        "Ошибка регистрации",
        getServerErrorMessage(error, "Не удалось завершить регистрацию"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const finish = () => {
    setShowSuccessModal(false);
    navigate(loginPath, { replace: true });
  };

  const headerContent = isCodeSent ? (
    <VerificationHint>
      Код отправлен на <strong>{registrationData?.email}</strong>. Введите его, чтобы завершить регистрацию.
    </VerificationHint>
  ) : !linkToken ? (
    <AuthRoleSwitch
      value={role}
      onChange={(nextRole) => {
        setRole(nextRole);
        setExternalErrors({});
      }}
      disabled={isLoading}
      label="Зарегистрироваться как"
    />
  ) : undefined;

  if (showSuccessModal && isTelegramStudentSignup) {
    return (
      <LinkPageShell>
        <LinkPageContent>
          <LinkPageTitle>Веб-кабинет ученика</LinkPageTitle>
          <SuccessState>
            <SuccessIconCircle aria-hidden="true">
              <CheckMarkIcon />
            </SuccessIconCircle>
            <LinkSectionTitle>Аккаунт создан</LinkSectionTitle>
            <SuccessText>
              Web-аккаунт создан и связан с Telegram. Откройте кабинет в браузере, чтобы продолжить.
            </SuccessText>
            <LinkPrimaryAction
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => openStudentCabinetInBrowser(studentBrowserDestination)}
            >
              Перейти в кабинет
            </LinkPrimaryAction>
            <LinkFootnote>Веб-кабинет откроется вне Telegram.</LinkFootnote>
          </SuccessState>
        </LinkPageContent>
      </LinkPageShell>
    );
  }

  return (
    <>
      <FormModal
        title={isCodeSent ? "Подтверждение почты" : linkToken ? "Доступ в веб-кабинет" : "Регистрация"}
        fields={fields}
        onSubmit={handleSubmit}
        footerLinks={footerLinks}
        submitButtonText={isCodeSent ? "Зарегистрироваться" : "Отправить код"}
        externalErrors={externalErrors}
        isLoading={isLoading}
        headerContent={headerContent}
      />
      {showSuccessModal && <SuccessModal message="Регистрация прошла успешно!" onClose={finish} />}
    </>
  );
};

export default RegistrationPage;